import React, { useState } from 'react';

import DataTable from '../shared/data_table';
import StatusPill from '../shared/status_pill';
import { Ledger, Note } from '../shared/cells';
import SessionDateLink from '../shared/session_date_link';
import WhyPopover from '../overview/why_popover';
import RuleRefLink from '../rules_engine/rule_ref';
import { ROW_ACTION } from './params';
import { list_row_view, split_actions } from './adapters';
import ActionsMenu from './actions_menu';
import { EM_DASH } from '../format';

/*
 * the month's penalties across teachers, one compact line per penalty (Ops option A); the caret
 * opens a detail row with everything else the row knows. Every button comes from the row's
 * editable.actions; the server's why_not says why the rest are missing (or why there are none).
 */
export const ACTION_LABELS = {
  [ROW_ACTION.CORRECT]: 'Correct',
  [ROW_ACTION.WAIVE]: 'Waive',
  [ROW_ACTION.UNWAIVE]: 'Undo waiver',
  [ROW_ACTION.REVERT]: 'Revert',
  [ROW_ACTION.REMOVE]: 'Remove',
  [ROW_ACTION.APPLY_PASS]: 'Use Free Pass',
  [ROW_ACTION.REMOVE_PASS]: 'Remove Free Pass'
};

// Cards only below this container width: 1366px at 125% zoom (~780px list) keeps the table.
const CARD_BELOW = 700;

const is_neg = (text) => /^[-−]/.test(String(text || ''));
const tone_of = (text) => (is_neg(text) ? 'neg' : '');
// An orphaned penalty's explanation is a state of the row, not an action note.
const orphan_note = (r) => (r.row && r.row.is_orphaned && r.why_not ? r.why_not : '');

// A labelled value in the detail row.
const Field = ({ label, wide, children }) => (
  <div className={`tp-pe-dt-f${wide ? ' tp-pe-dt-wide' : ''}`}>
    <dt className="tp-pe-dt-k">{label}</dt>
    <dd className="tp-pe-dt-v">{children}</dd>
  </div>
);

// Everything the old multi-line row showed beyond the list's columns; nothing dropped.
const PenaltyDetail = ({ r, lv, on_error }) => {
  const d = lv.detail;
  const by = [r.approved_by ? `Approved by ${r.approved_by}` : '', r.updated_text]
    .filter(Boolean)
    .join(' · ');
  const has_note = r.reason_text || r.note || by;
  return (
    <dl className="tp-pe-dt" id={`tp-pe-dt-${r.uuid}`} data-detail-for={r.uuid}>
      <Field label="Rule">
        {r.rule_ref ? <RuleRefLink rule_ref={r.rule_ref} on_error={on_error} /> : EM_DASH}
      </Field>
      <Field label="Penalty ID">
        <span className="tp-mono" title={r.uuid}>
          {d.penalty_id}
        </span>
      </Field>
      <Field label="Session">
        {d.session_text ? (
          <SessionDateLink uuid={r.session_uuid} kind={r.session_kind}>
            {d.session_text}
          </SessionDateLink>
        ) : (
          'Month-level'
        )}
      </Field>
      <Field label="Source">{d.source_text}</Field>
      <Field label="Money">
        <Ledger
          items={[
            { label: 'Base rate', value: r.base_rate_text },
            { label: 'Pen %', value: r.pct_text },
            { label: 'Original', value: r.original_text, tone: tone_of(r.original_text) },
            r.corrected_text
              ? { label: 'Corrected', value: r.corrected_text, tone: tone_of(r.corrected_text) }
              : null
          ]}
          total={{ label: 'Charged', tone: r.charged ? 'neg' : 'zero', value: r.charged_text }}
        />
      </Field>
      {d.status_lines.length ? (
        <Field label="Status detail">
          {d.status_lines.map((l) => (
            <div key={l}>{l}</div>
          ))}
        </Field>
      ) : null}
      {(r.join_lines || []).length ? (
        <Field label="Join times">
          {r.join_lines.map((l) => (
            <div key={l.label} className="tp-pe-join-time" data-join-time={l.label}>
              {l.label}: {l.value}
            </div>
          ))}
        </Field>
      ) : null}
      {d.actions_note ? <Field label="Actions">{d.actions_note}</Field> : null}
      {has_note || orphan_note(r) ? (
        <Field label="Reason / note" wide>
          {has_note ? (
            <span title={r.updated_by_title}>
              <Note label={r.reason_text} text={r.note} by={by} />
            </span>
          ) : null}
          {orphan_note(r) ? <Note text={orphan_note(r)} warn /> : null}
        </Field>
      ) : null}
    </dl>
  );
};

// Card blocks carry a visible label (the table has column headers instead).
const CardLabel = ({ children }) => (
  <span className="tp-pe-cl" aria-hidden="true">
    {children}
  </span>
);

const PenaltyTable = ({
  rows,
  selectable = true,
  selected = {},
  on_toggle,
  on_toggle_all,
  on_action,
  highlight_uuid,
  empty_text,
  sort,
  on_sort,
  on_sort_set,
  on_error,
  now
}) => {
  const [expanded, setExpanded] = useState({});
  const toggle_open = (r) => setExpanded((e) => ({ ...e, [r.uuid]: !e[r.uuid] }));
  const lv_cache = new Map();
  const lv = (r) => {
    if (!lv_cache.has(r.uuid)) lv_cache.set(r.uuid, list_row_view(r, now));
    return lv_cache.get(r.uuid);
  };

  const action_buttons = (r, list = r.actions) =>
    list.map((a) => (
      <button
        key={a}
        type="button"
        className="tp-pe-act-btn"
        aria-label={`${ACTION_LABELS[a]} ${r.aria_name}`}
        onClick={() => on_action(a, r)}
      >
        {ACTION_LABELS[a]}
      </button>
    ));
  const no_actions = (r) => (
    <span className="tp-sub tp-pe-why">
      {orphan_note(r) ? 'No changes allowed' : r.why_not || 'No changes allowed'}
    </span>
  );
  // Table: up to two buttons on the row's one line, the rest under More.
  const actions_cell = (r) => {
    if (!r.actions.length) return no_actions(r);
    const { inline, more } = split_actions(r.actions, ACTION_LABELS);
    return (
      <span className="tp-pe-acts tp-actions" data-actions-for={r.uuid}>
        {action_buttons(r, inline)}
        {more.length ? (
          <ActionsMenu
            label={`More actions for ${r.aria_name}`}
            items={more.map((a) => ({
              key: a,
              text: ACTION_LABELS[a],
              aria_label: `${ACTION_LABELS[a]} ${r.aria_name}`,
              on_select: () => on_action(a, r)
            }))}
          />
        ) : null}
      </span>
    );
  };
  // Cards have room: every action is a button.
  const actions_card = (r) =>
    r.actions.length ? (
      <span className="tp-pe-acts tp-actions" data-actions-for={r.uuid}>
        {action_buttons(r)}
      </span>
    ) : (
      no_actions(r)
    );

  const all_on = rows.length > 0 && rows.every((r) => selected[r.uuid]);
  const select_column = {
    key: 'select',
    role: 'select',
    track: '34px',
    label: (
      <input
        type="checkbox"
        aria-label="Select every listed penalty"
        checked={all_on}
        onChange={() => on_toggle_all(!all_on)}
      />
    ),
    render: (r) => (
      <input
        type="checkbox"
        aria-label={`Select ${r.aria_name}`}
        checked={!!selected[r.uuid]}
        onChange={() => on_toggle(r)}
      />
    ),
    hideInCard: true
  };

  const student_cell = (r) => {
    const s = lv(r).student;
    return (
      <div className="tp-pe-one" title={s.kind_text ? `${s.kind_text} ${s.name}` : s.name}>
        {s.href ? (
          <a className="tp-link" href={s.href} target="_blank" rel="noopener noreferrer">
            {s.name}
          </a>
        ) : (
          <span>{s.name}</span>
        )}
        {s.kind_text ? <span className="sr-only">, {s.kind_text}</span> : null}
      </div>
    );
  };
  const class_cell = (r) => {
    const v = lv(r);
    return (
      <span className="tp-pe-when tp-pe-one" title={r.session_text}>
        <span>{v.class_time ? `${v.class_day},` : v.class_day}</span>
        {v.class_time ? ' ' : null}
        {v.class_time ? <span>{v.class_time}</span> : null}
      </span>
    );
  };
  const penalty_cell = (r) => (
    <button
      type="button"
      className="tp-pe-caret"
      aria-expanded={expanded[r.uuid] ? 'true' : 'false'}
      aria-controls={expanded[r.uuid] ? `tp-pe-dt-${r.uuid}` : undefined}
      aria-label={`${r.type_text}: ${expanded[r.uuid] ? 'hide' : 'show'} details`}
      onClick={() => toggle_open(r)}
    >
      <span className="tp-pe-caret-i" aria-hidden="true">
        {expanded[r.uuid] ? '▾' : '▸'}
      </span>
      <span className="tp-pe-one" title={r.type_text}>
        {r.type_text}
      </span>
    </button>
  );
  const amount_cell = (r) => (
    <span className={r.charged ? 'tp-neg' : undefined}>
      <WhyPopover detail={r.explain} amount_node={<span>{r.charged_text}</span>} />
    </span>
  );
  // One short chip; the month-review flag rides on the teacher's name in the table.
  const status_cell = (r) => (
    <span className="tp-pe-st" title={r.waiver_text}>
      <StatusPill tone={r.waiver_tone}>{lv(r).status_chip}</StatusPill>
    </span>
  );
  const status_card = (r) => (
    <div className="tp-pe-st">
      <StatusPill tone={r.waiver_tone}>{r.waiver_text}</StatusPill>
      {r.needs_review ? <StatusPill tone="orange">Month needs review</StatusPill> : null}
    </div>
  );
  const teacher_cell = (r) => (
    <span className="tp-pe-one tp-pe-teacher" title={r.teacher_name}>
      {r.needs_review ? (
        <span className="tp-pe-flag" title="Month needs review">
          !<span className="sr-only">Month needs review</span>
        </span>
      ) : null}
      {r.teacher_name}
    </span>
  );

  const columns = [
    ...(selectable ? [select_column] : []),
    {
      key: 'teacher_name',
      role: 'identifier',
      label: 'Teacher',
      track: { compact: '10%', full: '12%' },
      sort_key: 'teacher_name',
      render: teacher_cell,
      card_render: (r) => <span className="tp-pe-teacher">{r.teacher_name}</span>
    },
    {
      key: 'student',
      label: 'Student',
      track: { compact: '10%', full: '12%' },
      sort_key: 'student_name',
      render: student_cell,
      card_render: (r) => (
        <>
          <CardLabel>Student</CardLabel>
          {student_cell(r)}
        </>
      )
    },
    {
      key: 'class',
      label: 'Class (IST)',
      track: { compact: '122px', full: '132px' },
      sort_key: 'session_time',
      render: class_cell,
      card_render: (r) => (
        <>
          <CardLabel>Class (IST)</CardLabel>
          {class_cell(r)}
        </>
      )
    },
    {
      key: 'type_text',
      label: 'Penalty',
      sort_key: 'type',
      render: penalty_cell,
      card_render: (r) => (
        <>
          <CardLabel>Penalty</CardLabel>
          {penalty_cell(r)}
        </>
      )
    },
    {
      key: 'charged_text',
      role: 'amount',
      label: 'Amount',
      align: 'right',
      track: { compact: '84px', full: '96px' },
      sort_key: 'charged',
      sorts: [
        { sort_key: 'base_rate', label: 'Base rate' },
        { sort_key: 'pct', label: 'Pen %' },
        { sort_key: 'original', label: 'Original' }
      ],
      render: amount_cell,
      card_render: (r) => (
        <>
          <CardLabel>Amount</CardLabel>
          {amount_cell(r)}
        </>
      )
    },
    {
      key: 'waiver_text',
      role: 'status',
      label: 'Status',
      track: { compact: '96px', full: '100px' },
      sort_key: 'status',
      render: status_cell,
      card_render: status_card
    },
    {
      key: 'actions',
      role: 'actions',
      label: 'Actions',
      track: { compact: '180px', full: '188px' },
      render: actions_cell,
      card_render: actions_card
    },
    {
      // the caret's detail row, spanning from the teacher column to the row's end
      key: 'detail',
      label: 'Details',
      line: { from: 'teacher_name', bare: true },
      render: (r) =>
        expanded[r.uuid] ? <PenaltyDetail r={r} lv={lv(r)} on_error={on_error} /> : null
    }
  ];

  return (
    <div className="tp-pe-list">
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.key}
        rowClassName={(r) =>
          [
            highlight_uuid && r.uuid === highlight_uuid ? 'tp-row-hl' : '',
            selected[r.uuid] ? 'tp-row-sel' : '',
            expanded[r.uuid] ? 'tp-pe-open' : ''
          ]
            .filter(Boolean)
            .join(' ')
        }
        sort={sort}
        on_sort={on_sort}
        on_sort_set={on_sort_set}
        emptyText={empty_text}
        select_all={
          selectable && rows.length
            ? {
                checked: all_on,
                on_change: (on) => on_toggle_all(on),
                label: 'Select every listed penalty'
              }
            : null
        }
        card_below={CARD_BELOW}
        responsive
      />
    </div>
  );
};

export default PenaltyTable;
